import { NotificationAction } from "@config/local-notifications/notification-actions";
import { NotificationIcon } from "@config/local-notifications/notification-icon";
import { organizationManagerNotificationRepo } from "@repositories";
import { Roles, type BaseRoles } from "@shared/Roles";
import { copyApiPrefilterPerRole } from "@shared/backend/entity-security/copy-api-prefilter-per-role";
import { formatDate } from "@shared/backend/format-date";
import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { awaiter } from "@shared/helpers/awaiter";
import { milis } from "@shared/helpers/duration";
import { textBuilder } from "@shared/helpers/text-builder";
import { Allow, remult } from "remult";
import { Association } from "../association";
import { Opportunity } from "../opportunity/entity";
import type { OrganizationManager } from "../organization-manager";
import { type OpportunityParticipant } from "./entity";

export const OpportunityParticipantEntityConfig: HyperEntityConfig<OpportunityParticipant> =
{
  allowApiCrud: Allow.authenticated,
  apiPrefilterPerRole: async (filter) => {
    filter(Roles.All, {
      $or: [
        {
          association: {
            $is: await copyApiPrefilterPerRole(Association),
          },
        },
        {
          opportunity: {
            $is: await copyApiPrefilterPerRole(Opportunity),
          },
        },
      ],
    });
  },

  allowInsertPerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );

    allow(Roles.OrganizationManagers, {
      opportunity: {
        $is: {
          organization: {
            $id: remult.user!.organizationId,
          },
        },
      },
    });
  },

  allowDeleteAndUpdatePerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );

    allow(Roles.OrganizationManagers, {
      opportunity: {
        $is: {
          organization: {
            $id: remult.user!.organizationId,
          },
        },
      },
    });
  },

  async saving(entity, e) {
    const groupId = (entity as OpportunityParticipant).groupId;
    if (!groupId || groupId === "") {
      delete (entity as Partial<OpportunityParticipant>).groupId;
      return;
    }
  },

  async onUpdate(entity, e) {
    // GroupId validation is handled in saving hook, no need to duplicate here
  },

  async onDelete(entity, e) {
    const [association, opportunity] = await awaiter(
      e.relations.association.findOne({
        include: {
          user: true,
        },
      }),
      e.relations.opportunity.findOne({
        include: {
          organization: {
            include: {
              managers: true,
            },
          },
          manager: true,
        },
      }),
    );

    if (!opportunity || !association?.user) return;

    const user = association.user;

    const managers = Array.from(
      new Set(
        [
          opportunity.manager,
          ...(opportunity.organization?.managers.filter((v) =>
            v.roles.some((role) => Roles.Admins.includes(role as BaseRoles)),
          ) ?? []),
        ].filter(Boolean) as OrganizationManager[],
      ),
    );

    const text = textBuilder()
      .if(user.gender === "MALE", "המתנדב")
      .if(user.gender === "FEMALE", "המתנדבת")
      .if(user.gender === "OTHER", "המתנדב/ת")
      .space()
      .add(user.name)
      .space()
      .add("ביטל את הרשמתו למשימה ")
      .space()
      .add(opportunity.title)
      .space()
      .if(!!opportunity.startDate, (b) =>
        b.add("שתתקיים ב").add(formatDate(opportunity.startDate!)),
      )
      .add(".")
      .done();

    await organizationManagerNotificationRepo.insert(
      managers.map((v) => ({
        toId: v.id,
        content: text,
        title: "ביטול רישום למשימה",
        icon: NotificationIcon.USER_CANCELED,
        actions: [
          {
            id: NotificationAction.NAVIGATE,
            btnText: "לפרטי המשימה",
            extra: {
              to: `/events/${opportunity.id}`,
            },
          },
        ],
      })),
    );
  },
};
